import React from "react";
import { styled } from "@mui/material/styles";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import { getPatientRegionsList } from "store/PatientRegionsStore/actions";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import { getClinicianPatientsList } from "store/ClinicianStore/actions";
import { Clinician } from "types/Clinician";
import {
  selectCurrentClinician,
  selectCurrentClinicianLoading,
} from "store/ClinicianStore/selectors";
import { selectLoading } from "store/PatientRegionsStore/selectors";
import { CircularProgress } from "@material-ui/core";
import { ManagementType } from "types/common";
import {
  setSearch,
  setOffsetClinicPatients,
} from "store/PatientStore/patientSlice";
import {
  setSearch as setSearchClinician,
  setOffset,
} from "store/ClinicianStore/clinicianSlice";
import { getClinicPatientsList } from "store/PatientStore/actions";
import { getCliniciansList } from "store/ClinicianStore/actions";
import { setOffset as setClinicianSliceOffset } from "store/ClinicianStore/clinicianSlice";
import { setOffset as setPatientRegionsOffset } from "store/PatientRegionsStore/patientRegionsSlice";

export const Search = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  flexDirection: "row",
  backgroundColor: "#F0F0F0",
  borderRadius: "10px",
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

export const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const StyledInputBase = styled(InputBase)(({ theme }) => ({
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(1)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
      "&:focus": {
        width: "20ch",
      },
    },
  },
}));

type Props = {
  isAthlete: boolean;
  managementType?: ManagementType;
};

export const SearchBoxBase = ({
  onChange,
  value,
  loading,
  onClear,
  style,
  onBlur,
  onKeyDown,
}: {
  onChange: any;
  value: any;
  loading: any;
  onClear: any;
  onBlur: any;
  style?: any;
  onKeyDown?: any;
}) => {
  return (
    <Search style={{ ...style }}>
      <StyledInputBase
        placeholder="Search"
        inputProps={{ "aria-label": "search", color: "#5C5D68" }}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={value}
        onBlur={onBlur}
      />
      <SearchIconWrapper>
        {loading ? (
          <CircularProgress color="inherit" size={15} />
        ) : value.length < 1 ? (
          <SearchIcon />
        ) : (
          <div onClick={onClear} aria-hidden="true">
            <CancelIcon />
          </div>
        )}
      </SearchIconWrapper>
    </Search>
  );
};

export const SearchBox = ({ isAthlete, managementType }: Props) => {
  const [inputValue, setInputValue] = React.useState<string>("");
  const currentClinician: Clinician | undefined = useAppSelector(
    selectCurrentClinician
  );
  const dispatch = useAppDispatch();
  const timeoutRef = React.useRef<NodeJS.Timeout | undefined>();
  const loadingPatients = useAppSelector(selectLoading);
  const loadingClinicianPatients = useAppSelector(
    selectCurrentClinicianLoading
  );
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const onTypeFinished = (searchingFor: string) => {
    clearTimeout(Number(timeoutRef.current));
    timeoutRef.current = setTimeout(() => searchMethod(searchingFor), 500);
  };
  const getClinicianListWithSearch = (search = "") => {
    dispatch(setSearchClinician(search));
    dispatch(setOffset(0));
    dispatch(getCliniciansList());
  };

  const getClinicPatientListWithSearch = (search = "") => {
    dispatch(setSearch(search));
    dispatch(setOffsetClinicPatients(0));
    dispatch(getClinicPatientsList());
  };

  const searchMethod = (searchingFor: string) => {
    dispatch(setPatientRegionsOffset(0));

    if (searchingFor && searchingFor.length > 1) {
      if (isAthlete) {
        dispatch(setClinicianSliceOffset(0));
        dispatch(getPatientRegionsList(searchingFor));
      } else if (managementType === ManagementType.PATIENT) {
        getClinicPatientListWithSearch(searchingFor);
      } else if (managementType === ManagementType.USER) {
        getClinicianListWithSearch(searchingFor);
      } else {
        dispatch(
          getClinicianPatientsList(
            currentClinician?._id ?? 0,
            () => {},
            searchingFor
          )
        );
      }
    } else {
      if (isAthlete) {
        dispatch(getPatientRegionsList(""));
      } else if (managementType === ManagementType.PATIENT) {
        getClinicPatientListWithSearch(searchingFor);
      } else if (managementType === ManagementType.USER) {
        getClinicianListWithSearch(searchingFor);
      } else {
        dispatch(
          getClinicianPatientsList(currentClinician?._id ?? 0, () => {}, "")
        );
      }
    }
  };

  const checkTypeStatus = (value: string) => {
    if (value.length > 2 && value.length != 0) {
      onTypeFinished(value);
    } else if (value.length == 0) {
      onTypeFinished("");
    }
  };

  const sanitizeInput = (input: string) => {
    //const sanitizedInput = input.replace(/[^a-zA-Z0-9]/g, '');
    return input;
  };

  const handleChange = (e: any) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setInputValue(sanitizedValue);
    checkTypeStatus(sanitizedValue);
  };

  React.useEffect(() => {
    return () => {
      switch (managementType) {
        case ManagementType.PATIENT:
          getClinicPatientListWithSearch();
          break;
        case ManagementType.USER:
          getClinicianListWithSearch();
          break;
        default:
          break;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!loadingPatients && !loadingClinicianPatients) {
      inputRef.current?.focus();
    }
  }, [loadingPatients, loadingClinicianPatients]);

  return (
    <Search aria-disabled={loadingPatients || loadingClinicianPatients}>
      <StyledInputBase
        placeholder="Search"
        inputProps={{ "aria-label": "search", color: "#5C5D68" }}
        onChange={handleChange}
        value={inputValue}
        disabled={loadingPatients || loadingClinicianPatients}
        inputRef={inputRef}
      />
      <SearchIconWrapper>
        {loadingPatients || loadingClinicianPatients ? (
          <CircularProgress color="inherit" size={15} />
        ) : inputValue.length < 1 ? (
          <SearchIcon />
        ) : (
          <div
            onClick={() => {
              setInputValue("");
              onTypeFinished("");
            }}
            aria-hidden="true"
          >
            <CancelIcon />
          </div>
        )}
      </SearchIconWrapper>
    </Search>
  );
};
